import * as React from "react";
import {css, Theme} from "@emotion/react";
import {carsTheme} from "@rw/themes";

import {isDefaultSellerCover, isDefaultSellerLogo, resolveSellerCoverUrl, resolveSellerLogoUrl} from "../utils/seller_default_images";

type SellerHeroProps = {
    name: string;
    locationLine?: string;
    offerCount: number;
    description?: string;
    coverUrl?: string | null;
    coverKey?: string | null;
    logoUrl?: string | null;
    logoKey?: string | null;
    /** Up to a few offer photos for the stock strip when cover is brand default. */
    stockPreviewImages?: string[];
    addressStreet?: string | null;
};

export const SellerHero = ({
    name,
    locationLine,
    offerCount,
    description,
    coverUrl,
    coverKey,
    logoUrl,
    logoKey,
    stockPreviewImages = [],
    addressStreet
}: SellerHeroProps) => {
    const cover = resolveSellerCoverUrl(coverUrl, coverKey);
    const logo = resolveSellerLogoUrl(logoUrl, logoKey);
    const logoIsDefault = isDefaultSellerLogo(logoUrl, logoKey);
    const coverIsBrandDefault = isDefaultSellerCover(coverUrl, coverKey);
    const offersLabel = offerCount === 1 ? "aktywna oferta" : "aktywnych ofert";
    const stockThumbs = stockPreviewImages.filter(Boolean).slice(0, 3);
    const showStockStrip = coverIsBrandDefault && stockThumbs.length > 0;

    return (
        <header css={heroRoot}>
            <div css={coverWrap}>
                <img src={cover} alt="" css={coverImg} />
                <div css={[coverFade, showStockStrip && coverFadeWithStrip]} />

                {showStockStrip ? (
                    <div css={stockStrip} aria-hidden>
                        {stockThumbs.map((src, index) => (
                            <div key={`${src}-${index}`} css={stockThumb}>
                                <img src={src} alt="" css={stockThumbImg} />
                            </div>
                        ))}
                    </div>
                ) : null}

                {coverIsBrandDefault ? <span css={coverBadge}>zakupauto.pl</span> : null}
            </div>

            <div css={identityPanel}>
                <div css={identityRow}>
                    <div css={[logoWrap, logoIsDefault && logoWrapDefault]}>
                        <img src={logo} alt={logoIsDefault ? "Zakupauto" : `Logo — ${name}`} css={logoIsDefault ? logoImgDefault : logoImgCustom} />
                    </div>

                    <div css={identityText}>
                        <p css={eyebrow}>Sprzedawca na Zakupauto</p>
                        <h1 css={title}>{name}</h1>

                        <div css={metaRow}>
                            {locationLine ? <span css={metaChip}>{locationLine}</span> : null}
                            {addressStreet ? <span css={metaChip}>{addressStreet}</span> : null}
                            <span css={offerChip}>
                                <strong>{offerCount}</strong> {offersLabel}
                            </span>
                        </div>
                    </div>
                </div>

                {description ? <p css={descriptionStyle}>{description}</p> : null}
            </div>
        </header>
    );
};

const heroRoot = css`
    margin: 0 0 2rem;
`;

const coverWrap = css`
    position: relative;
    width: 100%;
    height: 168px;
    overflow: hidden;
    background: #1f2430;
    border-radius: 10px 10px 0 0;

    @media (min-width: 768px) {
        height: 240px;
    }
`;

const coverImg = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
`;

const coverFade = css`
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(15, 18, 24, 0.08) 35%, rgba(15, 18, 24, 0.4) 100%);
    pointer-events: none;
`;

const coverFadeWithStrip = css`
    background: linear-gradient(90deg, rgba(15, 18, 24, 0.15) 0%, rgba(15, 18, 24, 0.05) 45%, rgba(15, 18, 24, 0.55) 72%, rgba(15, 18, 24, 0.72) 100%);
`;

const stockStrip = css`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: none;
    flex-direction: column;
    gap: 8px;
    z-index: 1;

    @media (min-width: 640px) {
        display: flex;
    }

    @media (min-width: 768px) {
        right: 18px;
        gap: 10px;
    }
`;

const stockThumb = css`
    width: 92px;
    height: 58px;
    border-radius: 6px;
    overflow: hidden;
    border: 2px solid rgba(255, 255, 255, 0.92);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
    background: #111;

    @media (min-width: 768px) {
        width: 120px;
        height: 72px;
    }
`;

const stockThumbImg = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const coverBadge = css`
    position: absolute;
    left: 12px;
    top: 12px;
    padding: 5px 9px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.92);
    color: ${carsTheme.colors.brand_black};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    z-index: 1;
`;

const identityPanel = css`
    position: relative;
    z-index: 1;
    margin-top: -28px;
    padding: 0 0 1.25rem;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, #ffffff 28%);
    border-radius: 0 0 10px 10px;
`;

const identityRow = css`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 0 0.15rem;

    @media (min-width: 768px) {
        gap: 1.35rem;
    }
`;

const logoWrap = css`
    flex-shrink: 0;
    width: 92px;
    height: 92px;
    margin-top: -36px;
    border-radius: 14px;
    border: 3px solid #fff;
    background: #fff;
    box-shadow: 0 8px 24px rgba(20, 24, 32, 0.16);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (min-width: 768px) {
        width: 112px;
        height: 112px;
        margin-top: -48px;
    }
`;

const logoWrapDefault = css`
    background: #fff7f2;
`;

/** Custom seller logos: contain so the mark stays fully visible in the square. */
const logoImgCustom = css`
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
    background: #fff;
`;

const logoImgDefault = css`
    width: 76%;
    height: auto;
    object-fit: contain;
    display: block;
`;

const identityText = css`
    flex: 1;
    min-width: 0;
    padding-top: 0.55rem;

    @media (min-width: 768px) {
        padding-top: 0.75rem;
    }
`;

const eyebrow = css`
    margin: 0 0 0.3rem;
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${carsTheme.colors.brand_primary};
    font-weight: 700;
`;

const title = css`
    margin: 0 0 0.7rem;
    font-size: 26px;
    font-weight: 800;
    color: ${carsTheme.colors.brand_black};
    line-height: 1.15;
    letter-spacing: -0.02em;
    text-wrap: balance;

    @media (min-width: 768px) {
        font-size: 34px;
    }
`;

const metaRow = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const metaChip = css`
    display: inline-flex;
    align-items: center;
    padding: 6px 11px;
    border-radius: 4px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.2;
`;

const offerChip = css`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 6px 12px;
    border-radius: 4px;
    background: #fff7f2;
    border: 1px solid rgba(232, 99, 42, 0.28);
    color: ${carsTheme.colors.brand_black};
    font-size: 13px;
    line-height: 1.2;

    strong {
        color: ${carsTheme.colors.brand_primary};
        font-weight: 800;
    }
`;

const descriptionStyle = (theme: Theme) => css`
    margin: 1.1rem 0 0;
    padding-top: 1rem;
    border-top: 1px solid #ececef;
    max-width: 42rem;
    font-size: 15px;
    line-height: 1.55;
    color: #444;
    white-space: pre-line;

    @media (min-width: ${theme.breakpoints.screen_md}) {
        font-size: 16px;
    }
`;
